import { useQuery } from "@tanstack/react-query";
import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { queryFnPOST } from "api/tanStackQuery/helpers";
import { PreviewStatus } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Preview";
import { PreviewResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import {
  TrainFormSchema,
  trainFormSchema,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainInfoRequest } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/requestSchema";
import { singleTrainFormToRequestBody } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/transformers";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { useEffect } from "react";

type PreviewProps = {
  formValues: TrainFormSchema;
  train: TrainIdentifier;
};

function getPreviewFormValues(formValues: TrainFormSchema): TrainFormSchema {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- single special case
  switch (formValues.type) {
    case TrainEventTypeEnum.TRAIN_CUSTOM:
      return formValues;

    default: {
      const newFormValues: TrainFormSchema = {
        ...formValues,
        dropsLogText: { ...formValues.dropsLogText, enabled: false },
      };

      return newFormValues;
    }
  }
}

export const useTrainInfoPreview = ({ formValues, train }: PreviewProps) => {
  const parsedForm = trainFormSchema.safeParse(
    getPreviewFormValues(formValues),
  );

  const {
    status,
    data: previewData,
    refetch,
  } = useQuery({
    queryKey: [formValues, train],
    refetchOnWindowFocus: false,
    enabled: false,
    queryFn: ({ signal }) => {
      const body: TrainInfoRequest | null = parsedForm.success
        ? singleTrainFormToRequestBody(parsedForm.data, train)
        : null;

      return queryFnPOST<PreviewResponse>({
        url: `${getBackendUrl()}/trainInformation/singleTrainPreview`,
        body,
        signal,
      });
    },
  });
  const previewStatus: PreviewStatus = parsedForm.success ? status : "idle";

  useEffect(() => {
    if (parsedForm.success) {
      void refetch();
    }
  }, [formValues]);

  return { previewData, previewStatus };
};
